import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';
import { AUTH_CONFIG } from '../config/constants';
import mockApi from '../api/mockApi';

/**
 * Custom hook for making API requests with enhanced error handling and retry logic
 * @param {string} endpoint - The API endpoint URL
 * @param {Object} options - Additional options for the API request
 */
const useApi = (endpoint, options = {}) => {
  const {
    method = 'get',
    body = null,
    params = null,
    enabled = true,
    maxRetries = AUTH_CONFIG.RETRY?.MAX_ATTEMPTS || 3,
    useMockOnFailure = true
  } = options;

  // State for tracking data, loading, and error states
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Track if the hook is enabled
  const [isEnabled, setEnabled] = useState(enabled);

  // Refs to track request state
  const cancelRequest = useRef(false);
  const controller = useRef(null);
  const prevEnabled = useRef(enabled);
  const requestAttempts = useRef(0);
  const mockAttempted = useRef(false);
  
  // Constants
  const MAX_ATTEMPTS = 3;

  // Function to fetch data from API
  const fetchData = useCallback(async (retryAttempt = 0) => {
    // Don't fetch if disabled
    if (!isEnabled) return null;
    
    // Prevent infinite loops by limiting request attempts
    if (requestAttempts.current >= MAX_ATTEMPTS) {
      console.warn(`Maximum request attempts (${MAX_ATTEMPTS}) reached for endpoint: ${endpoint}`);
      setLoading(false);
      setError({ message: `Request failed after ${MAX_ATTEMPTS} attempts` });
      return null;
    }
    
    // Increment attempt counter
    requestAttempts.current += 1;
    
    // Cancel previous request if it's still ongoing
    if (controller.current) {
      controller.current.abort();
    }

    // Create a new AbortController for this request
    controller.current = new AbortController();
    cancelRequest.current = false;

    // Start loading
    setLoading(true);

    try {
      // Make the API request with the signal
      const response = await api.request({
        url: endpoint,
        method: method,
        params: params,
        data: body,
        signal: controller.current.signal
      });

      // Reset attempt counter on success
      requestAttempts.current = 0;
      mockAttempted.current = false;
      
      // Update state with response data
      setData(response.data);
      setLoading(false);
      setError(null);
      return response.data;
    } catch (err) {
      // Try to use mock data if enabled and not already attempted
      if (useMockOnFailure && !mockAttempted.current) {
        mockAttempted.current = true;
        try {

          const mockResponse = await mockApi(endpoint);
          if (mockResponse) {

            setData(mockResponse);
            setLoading(false);
            setError(null);
            return mockResponse;
          }
        } catch (error) { /* Error handled */ }
      }

      // Ignore errors from cancelled requests
      if (err.name === 'AbortError' || err.name === 'CanceledError' || cancelRequest.current || err.isCanceled) {
        setLoading(false);
        return null;
      }

      // Format error message
      let errorMessage = err?.message || err?.toString() || 'Unknown error';
      
      if (err.response) {
        // Server responded with an error status
        errorMessage = err.response.data?.error || err.response.data?.message || `Server error: ${err.response.status}`;
        
        // Only log errors that aren't 404 (404s are expected for optional endpoints)
        if (err.response.status !== 404) {
          console.error('Server Error:', err.response);
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error or timeout: ' + (err.message || 'Request failed');
        console.error('Network error or timeout:', err.message || 'Request failed');
        console.error('Request details:', {
          url: endpoint,
          baseURL: err.config?.baseURL,
          method: err.config?.method,
          fullURL: err.config?.baseURL + endpoint
        });
      } else {
        // Something happened in setting up the request
        errorMessage = 'Request error: ' + (err.message || err.toString() || 'Unknown error');
        console.error('Request setup error:', err);
      }

      // Check if we should retry
      const shouldRetry = retryAttempt < maxRetries;

      if (shouldRetry) {
        console.log(`Retrying request (attempt ${retryAttempt + 1}/${maxRetries})...`);
        const delay = 1000 * Math.pow(1.5, retryAttempt);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchData(retryAttempt + 1);
      } else {
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    }
  }, [endpoint, method, body, params, isEnabled, maxRetries, useMockOnFailure]);

  // Run fetch when component mounts or dependencies change
  useEffect(() => {
    // Reset counters when endpoint changes
    requestAttempts.current = 0;
    mockAttempted.current = false;
    
    // Only fetch if enabled
    if (!isEnabled) {
      setLoading(false);
      return;
    }
    
    // Fetch data
    fetchData(0);

    // Cleanup function to cancel ongoing requests
    return () => {
      cancelRequest.current = true;
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, [endpoint, retryCount, isEnabled, fetchData]);

  // Retry function
  const retry = useCallback(() => {
    requestAttempts.current = 0;
    mockAttempted.current = false;
    setRetryCount(prev => prev + 1);
  }, []);

  // Refetch function
  const refetch = useCallback(async () => {
    requestAttempts.current = 0;
    mockAttempted.current = false;
    setRetryCount(0);
    return fetchData(0);
  }, [fetchData]);

  // Enable function to manually enable the API call
  const enable = useCallback(() => {
    setEnabled(true);
  }, []);

  return { data, loading, error, retry, refetch, enable };
};

export default useApi;
